import { useLayoutEffect, useRef } from "react";
import type { InboxSearch } from "./scope";

/** The URL keys that only say what is open beside the list: a conversation, or a chat or task. */
const OPEN_KEYS: ReadonlySet<string> = new Set(["t", "a"]);

/**
 * Whether a change to the inbox URL starts the page at the top. A different list (scope, filter,
 * tag, search) does; opening or closing a conversation, chat or task (`t` and `a` alone; opening
 * one clears the other) does not, so the list stays where it was scrolled to.
 */
export function resetsScroll(patch: Partial<InboxSearch>): boolean {
  return Object.keys(patch).some((k) => !OPEN_KEYS.has(k));
}

/** The split pane's breakpoint (`lg`): from here the list and the conversation sit side by side. */
const SPLIT = "(min-width: 1024px)";

/**
 * The page scrolls (the window), and narrower than `lg` the conversation replaces the list. So
 * there, opening one starts it at the top and going back returns the list to where it was; side by
 * side, nothing moves.
 */
export function useListScroll(open: boolean): void {
  const saved = useRef<number | null>(null);
  const was = useRef(open);
  useLayoutEffect(() => {
    if (was.current === open) return;
    was.current = open;
    if (typeof window.matchMedia === "function" && window.matchMedia(SPLIT).matches) return;
    if (open) {
      saved.current = window.scrollY;
      window.scrollTo(0, 0);
      return;
    }
    if (saved.current !== null) window.scrollTo(0, saved.current);
    saved.current = null;
  }, [open]);
}
